import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {StudioAgentEntry, StudioCatalogue} from './useNovaStudioShell';

const Panel = styled.div`
	width: 280px;
	border-right: 1px solid #e6e8f0;
	display: flex;
	flex-direction: column;
	overflow-y: auto;
`;
const PanelHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px;
	font-weight: 600;
`;
const HeaderTitleGroup = styled.span`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const NewAgentButton = styled.button`
	border: none;
	background: transparent;
	color: #5b6af0;
	font-weight: 600;
	font-size: 13px;
	cursor: pointer;
	padding: 0;
`;
const CategoryName = styled.div`
	padding: 8px 16px;
	font-size: 12px;
	text-transform: uppercase;
	color: #6b7280;
`;
const AgentRowWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;
const AgentRow = styled.button<{$selected: boolean}>`
	display: block;
	flex-grow: 1;
	text-align: left;
	padding: 8px 16px;
	border: none;
	cursor: pointer;
	background: ${props => (props.$selected ? '#f0ecff' : 'transparent')};
`;
const EditAgentButton = styled.button`
	border: none;
	background: transparent;
	color: #6b7280;
	font-size: 12px;
	cursor: pointer;
	padding: 4px 12px;
`;

interface Props {
	catalogue: StudioCatalogue;
	selectedAgentId: string | null;
	onSelectAgent: (agentId: string) => void;
	onCreateAgent: () => void;
	onEditAgent: (agentId: string) => void;
}

export function NovaStudioCatalogue({
	catalogue,
	selectedAgentId,
	onSelectAgent,
	onCreateAgent,
	onEditAgent,
}: Props): JSX.Element {
	const {formatMessage} = useIntl();
	return (
		<Panel data-cy="nova-studio-catalogue">
			<PanelHeader>
				<HeaderTitleGroup>
					<span>Agents</span>
					<span data-cy="nova-studio-agent-count">{catalogue.totalAgentCount}</span>
				</HeaderTitleGroup>
				<NewAgentButton type="button" data-cy="nova-new-agent" data-testid="nova-new-agent" onClick={onCreateAgent}>
					{formatMessage({id: 'nova.catalogue.newAgent', defaultMessage: '+ New agent'})}
				</NewAgentButton>
			</PanelHeader>
			{catalogue.groups.map(group => (
				<div key={group.category.categoryId} data-cy={`nova-category-${group.category.categoryId}`}>
					<CategoryName>{group.category.name}</CategoryName>
					{group.agents.map((agent: StudioAgentEntry) => (
						<AgentRowWrapper key={agent.agentId}>
							<AgentRow
								$selected={agent.agentId === selectedAgentId}
								onClick={() => onSelectAgent(agent.agentId)}
							>
								{agent.name}
							</AgentRow>
							<EditAgentButton
								type="button"
								data-cy={`nova-edit-agent-${agent.agentId}`}
								data-testid={`nova-edit-agent-${agent.agentId}`}
								onClick={event => {
									event.stopPropagation();
									onEditAgent(agent.agentId);
								}}
							>
								{formatMessage({id: 'nova.catalogue.editAgent', defaultMessage: 'Edit'})}
							</EditAgentButton>
						</AgentRowWrapper>
					))}
				</div>
			))}
		</Panel>
	);
}
